import { ReactNode, lazy, Suspense, createContext, useContext, useState, useMemo, useEffect } from "react";
import Header from "./Header";
import { useLocation } from "@/lib/router-compat";
import MobileBottomNav from "./MobileBottomNav";
import ShareButton from "./ShareButton";
import WidgetZone from "@/components/ui/WidgetZone";
import SmartSearchBar from "@/components/SmartSearchBar";
import LazySection from "@/components/ui/LazySection";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";

type ChatState = { isOpen: boolean; isFullscreen: boolean };
type ChatContextType = {
  chat: ChatState;
  setChat: React.Dispatch<React.SetStateAction<ChatState>>;
};
const ChatContext = createContext<ChatContextType>({
  chat: { isOpen: false, isFullscreen: false },
  setChat: () => {},
});
export const useChatContext = () => useContext(ChatContext);

/** Retry dynamic import once with a full page reload to bust stale chunks */
function lazyRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() =>
      // First failure: wait briefly and retry in-place (handles transient
      // ERR_TIMED_OUT on chunk fetches without a hard reload).
      new Promise((r) => setTimeout(r, 800))
        .then(factory)
        .catch(() => {
          const key = "chunk-reload";
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            window.location.reload();
          }
          // Final fallback: no-op component to prevent blank screen
          return factory().catch(() => ({ default: () => null }));
        })
    )
  );
}

const Footer = lazyRetry(() => import("./Footer"));
const ChatWidget = lazyRetry(() => import("@/components/ChatWidget"));
const ChatEngagementPrompt = lazyRetry(() => import("@/components/ChatEngagementPrompt"));

import InteractionGate from "@/components/ui/InteractionGate";
import { useThemeLayout } from "@/hooks/useThemeLayout";

// Modern header is imported directly so its logo/nav arrive in the first HTML
// chunk (a lazy boundary streamed a blank placeholder first). Footer stays lazy.
import CarmaxHeader from "@/components/carmax/CarmaxHeader";
const CarmaxFooter = lazy(() => import("@/components/carmax/CarmaxFooter"));

const SCHEMA_DAYS = [
  "https://schema.org/Sunday",
  "https://schema.org/Monday",
  "https://schema.org/Tuesday",
  "https://schema.org/Wednesday",
  "https://schema.org/Thursday",
  "https://schema.org/Friday",
  "https://schema.org/Saturday",
];

const DEFAULT_HOURS_SPEC = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: SCHEMA_DAYS.slice(1, 7), // Mon-Sat
  opens: "T08:00",
  closes: "T16:00",
};

function buildHoursSpec(rows: { day_of_week: number; open_time: string; close_time: string }[]) {
  if (!rows || rows.length === 0) return DEFAULT_HOURS_SPEC;
  return rows.map((r) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: SCHEMA_DAYS[r.day_of_week] ?? SCHEMA_DAYS[1],
    opens: `T${r.open_time}`,
    closes: `T${r.close_time}`,
  }));
}

function buildJsonLd(salesHours: any, serviceHours: any) {
  return {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: "Rex & Sons RVs Castle Hayne NC",
    image: "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets/logo-400.webp",
    logo: "https://www.rexandsonsrvs.com/favicon-512.png",
    "@id": "https://www.rexandsonsrvs.com/",
    url: "https://www.rexandsonsrvs.com/",
    telephone: "(910) 793-2894",
    address: {
      "@type": "PostalAddress",
      streetAddress: "5625 Barbados Blvd",
      addressLocality: "Castle Hayne",
      addressRegion: "NC",
      postalCode: "28429",
      addressCountry: { "@type": "Country", name: "US" },
    },
    openingHoursSpecification: salesHours,
    sameAs: [
      "https://www.facebook.com/RexAndSonsRV",
      "https://www.instagram.com/rexandsons_rv",
      "https://www.youtube.com/playlist?list=UUOMXdujngOKkWVso3syNywA",
    ],
    department: [
      {
        "@type": "AutoRepair",
        name: "Rex & Sons RVs Service Department",
        image: "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets/logo-400.webp",
        telephone: "(910) 793-2894",
        address: {
          "@type": "PostalAddress",
          streetAddress: "5625 Barbados Blvd",
          addressLocality: "Castle Hayne",
          addressRegion: "NC",
          postalCode: "28429",
          addressCountry: { "@type": "Country", name: "US" },
        },
        openingHoursSpecification: serviceHours,
      },
      {
        "@type": "AutoPartsStore",
        name: "Rex & Sons RV Parts Store",
        image: "https://cpkvmigljcrjxctoixoq.supabase.co/storage/v1/object/public/splash-assets/logo-400.webp",
        telephone: "(910) 679-6522",
        address: {
          "@type": "PostalAddress",
          streetAddress: "5625 Barbados Blvd",
          addressLocality: "Castle Hayne",
          addressRegion: "NC",
          postalCode: "28429",
          addressCountry: { "@type": "Country", name: "US" },
        },
        openingHoursSpecification: salesHours,
      },
    ],
  };
}

interface LayoutProps {
  children: ReactNode;
  /** Optional replacement footer (used by alternate theme layouts). */
  footer?: ReactNode;
  /** Optional replacement header (sticky, in-flow — no top offset). */
  header?: ReactNode;
}

const Layout = ({ children, footer: footerProp, header: headerProp }: LayoutProps) => {
  // Site-wide chrome follows the theme's layout (classic on SSR/first paint).
  const themeLayout = useThemeLayout();
  const isCarmax = themeLayout === "carmax";
  const header = headerProp ?? (isCarmax ? (
    <CarmaxHeader />
  ) : undefined);
  const footer = footerProp ?? (isCarmax ? (
    <Suspense fallback={null}><CarmaxFooter /></Suspense>
  ) : undefined);
  const [chat, setChat] = useState<ChatState>({ isOpen: false, isFullscreen: false });
  const [hoursReady, setHoursReady] = useState(false);

  const pushClass = chat.isOpen
    ? chat.isFullscreen
      ? "mr-[100vw] lg:mr-[100vw]"
      : "mr-[420px] max-lg:mr-0"
    : "";

  // Defer business-hours fetches (JSON-LD only — invisible above-the-fold)
  // until first user interaction. Bots receive the static DEFAULT_HOURS_SPEC,
  // so humans can wait until they engage with the page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const events: (keyof WindowEventMap)[] = ["wheel", "pointerdown", "touchstart", "keydown"];
    const activate = () => {
      setHoursReady(true);
      events.forEach((e) => window.removeEventListener(e, activate));
    };
    events.forEach((e) => window.addEventListener(e, activate, { passive: true, once: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, activate));
    };
  }, []);

  // Combined fetch — one query for both departments instead of two parallel calls.
  const { data: hoursRows } = useQuery({
    queryKey: ["business-hours", "all"],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("business_hours")
        .select("*")
        .in("department", ["sales", "service"])
        .order("day_of_week");
      return data ?? [];
    },
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    enabled: hoursReady,
  });
  const salesRows = useMemo(
    () => (hoursRows ?? []).filter((r: any) => r.department === "sales"),
    [hoursRows]
  );
  const serviceRows = useMemo(
    () => (hoursRows ?? []).filter((r: any) => r.department === "service"),
    [hoursRows]
  );

  const jsonLd = useMemo(
    () => buildJsonLd(buildHoursSpec(salesRows ?? []), buildHoursSpec(serviceRows ?? [])),
    [salesRows, serviceRows]
  );

  const { pathname } = useLocation();
  // Event / custom template pages render their own full-bleed hero that must sit
  // under the transparent header, so no top offset there.
  const fullBleedTop = pathname.startsWith("/p/");
  // Smart search bar sits under the header on dealership pages; never on parts or admin pages.
  const showSmartSearch = !fullBleedTop && !/parts/i.test(pathname) && !pathname.startsWith("/admin") && !pathname.startsWith("/brochure-test");

  return (
    <ChatContext.Provider value={{ chat, setChat }}>
      <div className={cn("flex min-h-screen flex-col transition-[margin] duration-300 ease-in-out", pushClass)}>
        {/* Dealer/service/parts JSON-LD. Rendered in-body (valid per
            schema.org and honored by Google) instead of through <Helmet>, so
            Layout no longer pulls react-helmet-async into every page's render
            path or forces a client-side head diff on hydration. */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        {/* PageTracker is now mounted globally in App.tsx so pages that don't
            wrap in <Layout> (e.g. parts-catalog routes) are still tracked. */}
        <InteractionGate>
          <WidgetZone zone="body_start_html_tag_after" />
        </InteractionGate>
        {header ?? <Header />}
        <InteractionGate>
          <WidgetZone zone="content_before" />
        </InteractionGate>
        <main
          className="flex-1 pb-[72px] lg:pb-0"
          style={fullBleedTop || header ? undefined : { paddingTop: 'var(--header-h, 78px)' }}
        >{showSmartSearch && <SmartSearchBar />}{children}</main>
        <InteractionGate>
          <WidgetZone zone="content_after" />
        </InteractionGate>
        <LazySection
          as="footer"
          rootMargin="0px"
          fallback={<div className="bg-secondary min-h-[820px] md:min-h-[760px]" />}
        >
          <Suspense fallback={<div className="bg-secondary min-h-[820px] md:min-h-[760px]" />}>
            {footer ?? <Footer />}
          </Suspense>
        </LazySection>
        <InteractionGate>
          <WidgetZone zone="body_end_html_tag_before" />
          <ShareButton />
        </InteractionGate>
        <MobileBottomNav />
      </div>
      <InteractionGate>
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
        {/* Own Suspense boundary so the engagement prompt isn't blocked by the
            heavier ChatWidget chunk — it must mount as soon as the gate opens
            to start its 5s dwell timer. */}
        <Suspense fallback={null}>
          <ChatEngagementPrompt />
        </Suspense>
      </InteractionGate>
    </ChatContext.Provider>
  );
};

export default Layout;
